import { useState, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, MicOff, Loader2, Sparkles, Palette, Type as TypeIcon, Megaphone, Search } from "lucide-react";
import { toast } from "sonner";
import { fadeUp, viewport } from "@/lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

const BusinessIntake = () => {
  const [transcript, setTranscript] = useState("");
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const toggleRecord = async () => {
    if (recording) {
      recorderRef.current?.stop();
      setRecording(false);
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Voice isn't supported on this browser — just type instead.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = window.MediaRecorder?.isTypeSupported?.("audio/webm") ? "audio/webm" : "audio/mp4";
      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mimeType });
        if (blob.size < 1000) {
          toast.error("Didn't catch that — try again.");
          return;
        }
        setTranscribing(true);
        try {
          const form = new FormData();
          form.append("file", blob, mimeType.includes("webm") ? "recording.webm" : "recording.mp4");
          const { data } = await axios.post(`${API}/voice/transcribe`, form, { headers: { "Content-Type": "multipart/form-data" } });
          setTranscript((prev) => (prev.trim() ? prev.trim() + " " : "") + (data.transcript || "").trim());
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Couldn't hear that clearly — try again or type instead.");
        } finally {
          setTranscribing(false);
        }
      };
      recorderRef.current = recorder;
      recorder.start();
      setRecording(true);
    } catch {
      toast.error("Please allow microphone access to speak instead.");
    }
  };

  const submit = async () => {
    if (transcript.trim().length < 10) {
      toast.error("Tell us a little more about your business first.");
      return;
    }
    if (recording) { recorderRef.current?.stop(); setRecording(false); }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/business-intake`, { transcript });
      setResult(data);
      setTimeout(() => document.querySelector("#intake-result")?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't build your kit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="intake" className="py-24 md:py-32" data-testid="business-intake">
      <div className="max-w-5xl mx-auto px-5 md:px-8">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport} className="text-center mb-12">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#ff0055]/15 border border-[#ff0055]/40 text-[#ff0055] text-xs font-semibold uppercase tracking-widest px-4 py-2 mb-6">
            <Sparkles size={14} /> You talk. We build.
          </div>
          <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl lg:text-6xl leading-tight mb-4">
            Tell us about your business in 60 seconds.
          </h2>
          <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
            Talk or type. Our AI instantly drafts your brand, website copy and launch marketing — a live preview of what you'd get.
          </p>
        </motion.div>

        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport} className="bg-[#121212] border border-white/10 rounded-2xl p-6 md:p-8" data-testid="intake-form">
          <textarea
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
            rows={5}
            placeholder="e.g. I'm Sarah, I've started a beauty business in Marlow. I do brows, lashes and facials, and I want it to feel calm and premium…"
            className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-5 py-4 text-white placeholder:text-zinc-600 focus:ring-2 focus:ring-[#ff0055] focus:border-transparent outline-none resize-none transition-shadow mb-4"
            data-testid="intake-textarea"
          />
          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={toggleRecord} disabled={transcribing} className={`inline-flex items-center justify-center gap-2 rounded-full font-bold uppercase tracking-widest text-sm px-6 py-4 transition-colors disabled:opacity-60 ${recording ? "bg-[#ff0055] text-white" : "border border-white/20 text-white hover:bg-white/5"}`} data-testid="intake-mic">
              {transcribing ? <><Loader2 size={18} className="animate-spin" /> Transcribing…</> : recording ? <><MicOff size={18} /> Stop</> : <><Mic size={18} /> Speak instead</>}
            </button>
            <button onClick={submit} disabled={loading || transcribing} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#ccff00] text-black font-bold uppercase tracking-widest text-sm px-8 py-4 hover:scale-[1.01] transition-transform disabled:opacity-60" data-testid="intake-submit">
              {loading ? <><Loader2 size={18} className="animate-spin" /> Building your kit…</> : <><Sparkles size={18} /> Build my brand preview</>}
            </button>
          </div>
          {recording && <p className="text-sm text-[#ff0055] mt-3 animate-pulse" data-testid="intake-recording">● Recording… speak naturally about your business, then press stop.</p>}
        </motion.div>

        <AnimatePresence>
          {result && (
            <motion.div id="intake-result" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="mt-8 space-y-6" data-testid="intake-result">
              <div className="bg-[#0a0a0a] border border-[#ccff00]/40 rounded-2xl p-6 md:p-8 volt-glow">
                <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Your brand, in seconds</p>
                <h3 className="font-display font-black text-3xl md:text-4xl mb-2">{result.business_name}</h3>
                <p className="text-[#ccff00] text-lg">{result.tagline}</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <Card icon={Palette} title="Brand kit">
                  <div className="flex gap-2 mb-4">
                    {(result.brand?.palette || []).map((c, i) => (
                      <div key={i} className="flex-1 text-center" data-testid={`intake-colour-${i}`}>
                        <div className="w-full h-14 rounded-lg border border-white/10 mb-1.5" style={{ background: c.hex }} />
                        <span className="text-[10px] text-zinc-500">{c.hex}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-zinc-400 flex items-center gap-2"><TypeIcon size={14} className="text-[#ccff00]" /> {result.brand?.fonts?.heading} / {result.brand?.fonts?.body} · {result.brand?.tone}</p>
                </Card>

                <Card icon={Search} title="SEO & Google">
                  <p className="font-medium text-white mb-1">{result.marketing?.seo?.title}</p>
                  <p className="text-sm text-zinc-400 mb-3">{result.marketing?.seo?.meta_description}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(result.marketing?.seo?.keywords || []).map((k, i) => (
                      <span key={i} className="text-xs rounded-full bg-white/5 border border-white/10 px-2.5 py-1 text-zinc-400">{k}</span>
                    ))}
                  </div>
                </Card>

                <Card icon={Sparkles} title="Website copy">
                  <p className="font-display font-bold text-lg mb-3">{result.pages?.home_hero}</p>
                  <p className="text-sm text-zinc-400 mb-3">{result.pages?.about}</p>
                  <ul className="text-sm text-zinc-300 space-y-1">
                    {(result.pages?.services || []).map((s, i) => <li key={i}>· {s}</li>)}
                  </ul>
                </Card>

                <Card icon={Megaphone} title="Launch marketing">
                  <p className="text-sm text-zinc-300 mb-1"><span className="text-[#ff0055]">IG:</span> {result.marketing?.instagram_bio}</p>
                  <p className="text-sm text-zinc-500 mb-3">{result.marketing?.google_description}</p>
                  <ul className="text-sm text-zinc-300 space-y-1.5">
                    {(result.marketing?.launch_posts || []).slice(0, 5).map((p, i) => <li key={i} className="border-l-2 border-[#ccff00]/40 pl-3">{p}</li>)}
                  </ul>
                </Card>
              </div>

              <div className="rounded-2xl bg-[#ccff00] text-black p-6 md:p-8 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                  <p className="font-display font-bold text-xl">This is just the preview.</p>
                  <p className="text-black/70 text-sm">We'll build the real thing — brand, website & full setup — live in 48 hours.</p>
                </div>
                <button onClick={() => go("#contact")} className="shrink-0 rounded-full bg-black text-[#ccff00] font-bold uppercase tracking-widest text-xs px-7 py-4 hover:scale-105 transition-transform" data-testid="intake-launch-cta">
                  Launch this for real
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

const Card = ({ icon: Icon, title, children }) => (
  <div className="bg-[#121212] border border-white/10 rounded-2xl p-6">
    <p className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 mb-4"><Icon size={15} className="text-[#ccff00]" /> {title}</p>
    {children}
  </div>
);

export default BusinessIntake;
